import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ContasPagarConfigService } from '../../../../shared/services/apis/financas/contas-pagar-config.service';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { Router } from '@angular/router';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { MatIconModule } from '@angular/material/icon';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { ContasPagarConfigRequest } from '../../../../shared/services/requests/financas/contas-pagar-config.request';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';

@Component({
  selector: 'app-config-contas-pagar',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatCardModule,
    HeaderComponent,
    MatDialogModule,
    MatIconModule,
    FooterActionComponent,
    LoadingBoxComponent,
  ],
  templateUrl: './config-contas-pagar.component.html',
  styleUrls: ['./config-contas-pagar.component.css'],
})
export class ConfigContasPagarComponent implements OnInit, AfterViewChecked {
  public form: FormGroup;
  public isLoading = false;
  public isSaving = false;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;
  public moduloEnum = ModuloEnum.Configuracoes;
  private focusEmail = false;

  @ViewChild('emailInput') emailInput!: ElementRef;

  constructor(
    private fb: FormBuilder,
    private configService: ContasPagarConfigService,
    private alertService: AlertService,
    private router: Router,
    private dialog: MatDialog
  ) {
    this.form = this.fb.group({
      isAvisarEmail: [false],
      email: [{ value: '', disabled: true }],
    });
  }

  ngOnInit(): void {
    this.carregarConfiguracoes();

    this.form.get('isAvisarEmail')?.valueChanges.subscribe(ativo => {
      const emailControl = this.form.get('email');
      if (ativo) {
        emailControl?.enable();
        emailControl?.setValidators([Validators.required, this.multipleEmailsValidator]);
        this.focusEmail = true;
      } else {
        emailControl?.disable();
        emailControl?.clearValidators();
      }
      emailControl?.updateValueAndValidity();
    });
  }

  ngAfterViewChecked(): void {
    if (this.focusEmail && this.emailInput) {
      this.emailInput.nativeElement.focus();
      this.focusEmail = false;
    }
  }

  carregarConfiguracoes() {
    this.isLoading = true;
    this.configService.get().subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Alerta', response.errorDescription, AlertType.warning);
          return;
        }

        this.form.patchValue({
          isAvisarEmail: response.contasPagarConfig.isAvisarEmail,
          email: response.contasPagarConfig.email,
        });
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao carregar configurações', AlertType.error);
      },
    });
  }

  salvar() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValues = this.form.getRawValue();

    // Se estiver desativando
    if (!formValues.isAvisarEmail) {
      const dialogRef = this.dialog.open(ConfirmModalComponent, {
        data: {
          title: 'Desativar alertas',
          description:
            'Deseja desativar os alertas de contas a pagar? Você deixará de receber notificações diárias sobre seus vencimentos.',
          buttonClose: 'Cancelar',
          buttonConfirm: 'Desativar',
        },
      });

      dialogRef.afterClosed().subscribe(resultado => {
        if (resultado?.confirm) {
          this.executarSalvar(formValues);
        } else {
          // Se cancelou, volta o toggle pra true
          this.form.patchValue({ isAvisarEmail: true });
        }
      });
    } else {
      this.executarSalvar(formValues);
    }
  }

  private executarSalvar(formValues: any) {
    this.isSaving = true;
    const requestData: ContasPagarConfigRequest = {
      isAvisarEmail: formValues.isAvisarEmail,
      email: formValues.isAvisarEmail ? formValues.email : null,
    };

    this.configService.put(requestData).subscribe({
      next: (response: any) => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Aviso!', response.errorDescription, AlertType.warning);
          return;
        }

        this.alertService.show('Sucesso', 'Configuração salva com sucesso!', AlertType.success);
        this.router.navigate(['/configuracoes/financas']);
      },
      error: () => {
        this.alertService.show('Erro', 'Erro ao conectar com a API', AlertType.error);
        this.isSaving = false;
      },
    });
  }

  multipleEmailsValidator(control: any) {
    if (!control.value) {
      return null;
    }

    const emails = control.value.split(';').map((e: string) => e.trim());
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

    for (let i = 0; i < emails.length; i++) {
      if (!emailPattern.test(emails[i])) {
        return { invalidMultipleEmails: true };
      }
    }

    return null;
  }

  voltar() {
    this.router.navigate(['/configuracoes/financas']);
  }
}
